/**
 * The view pieces every tab shares: how a figure becomes a tile or a row of
 * facts, how a table sorts and searches, how a day reads on a chart. One
 * copy, so they look and behave the same on every tab.
 *
 * Nothing here works out a figure. Figures come from selectors.ts.
 */
/* eslint-disable react-refresh/only-export-components -- one module for the tabs' shared view code: helpers beside the components they feed. A change here reloads the tabs. */
import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import type { ChartOptions, Scale } from 'chart.js';
import { Form, Spinner } from 'react-bootstrap';
import { CaretDownFill, CaretUpFill, ExclamationTriangleFill } from 'react-bootstrap-icons';
import { formatNzDateTime, timeAgo } from '@/utils/relativeTime';
import type { ClientSnapshot } from '@/types/fleetAnalytics';
import type { FleetData } from '../fleetData';
import { isReady, kpiProps } from '../selectors';
import type { Figure, InactiveSpend, Spend, Tiered } from '../selectors';
import { dayWords, fmtUSD, getCurrencyState } from '../shared';
import type { WindowState } from '../shared';
import { ScopeTag } from '../ui/kit';
import type { FigureMarks, Scope } from '../ui/kit';
import type { Selection } from './registry';
import './common.css';

// ─── words ───────────────────────────────────────────────────────────────

/** A day of the window as it reads on a chart axis: "13 Oct". */
export const dayLabel = (day: string): string => dayWords(day, false);

/** The days the window covers, in words: "14 Sep 2026 to 13 Oct 2026". */
export function windowInWords(win: WindowState): string {
  return win.startDate === win.endDate
    ? dayWords(win.startDate)
    : `${dayWords(win.startDate)} to ${dayWords(win.endDate)}`;
}

/** "1 stack", "12 stacks": a count with the right form of its word. */
export const plural = (n: number | undefined, one: string, many = `${one}s`): string =>
  `${Math.round(n ?? 0).toLocaleString()} ${n === 1 ? one : many}`;

/** Credits carry a fraction: two decimals under 100, one above. */
export const fmtCredits = (n: number): string =>
  n.toLocaleString(undefined, { maximumFractionDigits: Math.abs(n) < 100 ? 2 : 1 });

/** Minutes as a person reads them: "45 min", "12 h 30 min", "3,147 h". */
export function fmtMinutes(minutes: number): string {
  const m = Math.round(minutes);
  if (m < 60) return `${m} min`;
  if (m >= 6000) return `${Math.round(m / 60).toLocaleString()} h`;
  const rest = m % 60;
  return rest ? `${Math.floor(m / 60)} h ${rest} min` : `${m / 60} h`;
}

/** A round step for an axis: 1, 2, 2.5 or 5 times a power of ten. */
function roundStep(raw: number): number {
  const pow = 10 ** Math.floor(Math.log10(raw));
  const f = raw / pow;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * pow;
}

type YScale = NonNullable<NonNullable<ChartOptions<'line'>['scales']>['y']>;

/**
 * The y-axis of a money chart. The data is in US dollars and the labels are in
 * the currency on screen. Left alone, chart.js picks round US dollar steps and
 * the labels read as odd amounts (NZ$2,857). This picks the steps in the
 * currency on screen, so the labels are round.
 */
export function moneyScale(): YScale {
  let peak = 0;
  let digits = 0;
  return {
    type: 'linear',
    beginAtZero: true,
    afterDataLimits(scale: Scale) {
      peak = Math.max(0, scale.max);
    },
    afterBuildTicks(scale: Scale) {
      const rate = getCurrencyState().rate || 1;
      const top = peak * rate;
      if (!(top > 0)) return;
      const step = roundStep(top / 5);
      digits = Math.min(4, Math.max(0, -Math.floor(Math.log10(step) + 1e-9)));
      if (!Number.isInteger(Number((step * 10 ** digits).toFixed(6)))) digits = Math.min(4, digits + 1);
      if (digits === 1) digits = 2; // money never shows one decimal place
      const count = Math.ceil(top / step - 1e-9);
      scale.ticks = Array.from({ length: count + 1 }, (_, i) => ({ value: (i * step) / rate }));
      scale.min = 0;
      scale.max = (count * step) / rate;
    },
    ticks: {
      callback: (v) => {
        const { rate, prefix } = getCurrencyState();
        return (
          prefix +
          (Number(v) * (rate || 1)).toLocaleString(undefined, {
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
          })
        );
      },
    },
  };
}

/** What a cell says when the number does not exist for the row. */
export function Quiet({ children = 'n/a', title }: { children?: ReactNode; title?: string }) {
  return (
    <span className="dash-quiet" title={title}>
      {children}
    </span>
  );
}

// ─── figures ─────────────────────────────────────────────────────────────

/** What a `<Kpi>` or a `<Fact>` takes from its figure: the number, the period it covers, and if it is exact. */
export interface TileProps extends FigureMarks {
  value: ReactNode;
}

/**
 * A figure as the props of a tile or of a row of facts. `explain` is what the
 * number means, shown on hover of its name. An approximate figure keeps its
 * number as it is: the kit marks it "approx." and gives the reason on hover.
 */
export function tile<T>(
  figure: Tiered<Figure<T>> | null | undefined,
  format: (value: NonNullable<T>) => string,
  explain?: string
): TileProps {
  return { ...kpiProps(figure, format), hint: explain };
}

/** The spinner a tile or a cell shows while its number loads. */
export function Waiting() {
  return <Spinner animation="border" size="sm" className="dash-spinner" role="status" aria-label="Loading" />;
}

/** The same as `tile`, for a figure that waits on heavier data: a small spinner until the data is in. */
export function waitingTile<T>(
  fleet: FleetData,
  figure: Tiered<Figure<T>>,
  format: (value: NonNullable<T>) => string,
  explain?: string
): TileProps {
  if (isReady(figure)) return tile(figure, format, explain);
  if (figure.needs.some((tier) => fleet.tiers[tier] === 'error')) {
    return { value: 'n/a', hint: 'This number did not load. Refresh the page to try again.' };
  }
  return { value: <Waiting />, hint: explain };
}

/**
 * A spend figure for a headline. AWS bills about two days late, so a window
 * that ends today always holds a day with no bill yet. That is said in a line
 * under the number (`notBilledWords`), and the number is marked approximate
 * only when it is short for another reason.
 */
export function settled<T>(figure: Figure<T>, spend: Spend): Figure<T> {
  return spend.notBilledYet && figure.approx ? { ...figure, approx: Boolean(spend.modelLines.approx) } : figure;
}

/** "1 day not billed yet", for the line under a spend figure. Nothing when every day of the window is billed. */
export function notBilledWords(spend: Spend): string | undefined {
  if (!spend.notBilledYet) return undefined;
  const { days } = spend.notBilledYet;
  return days > 0 ? `${plural(days, 'day')} not billed yet` : 'Some accounts not billed yet for the latest days';
}

/**
 * "Includes NZ$120.00 for 3 Inactive stacks", for the line under a spend
 * figure of a view that hides Inactive stacks. `part` names which part of
 * their spend the figure holds. Nothing when that part is under a cent.
 */
export function inactiveSpendWords(
  inactive: InactiveSpend | null | undefined,
  part: 'spend' | 'awsBill' | 'chat'
): string | undefined {
  if (!inactive) return undefined;
  const usd = inactive[part];
  if (Math.abs(usd) < 0.005) return undefined;
  return `Includes ${fmtUSD(usd)} for ${plural(inactive.stacks.length, 'Inactive stack')}`;
}

/** A figure's quiet lines, one under the other. Lines that are empty are left out. */
export function subLines(...lines: Array<string | undefined | false | null>): ReactNode {
  const kept = lines.filter((line): line is string => Boolean(line));
  if (kept.length === 0) return undefined;
  if (kept.length === 1) return kept[0];
  return kept.map((line) => (
    <span key={line} className="dash-subline">
      {line}
    </span>
  ));
}

/** A strip's column count for `n` figures, so the strip is one full row. */
export const gridCols = (n: number): 2 | 3 | 4 | 5 | 6 => (n <= 2 ? 2 : n === 3 ? 3 : n === 4 ? 4 : n === 5 ? 5 : 6);

// ─── rows ────────────────────────────────────────────────────────────────

/**
 * The stacks to build rows from. For a view: its stacks. For one stack: that
 * stack and the others on its AWS account, so its row can say who it shares
 * the account with.
 */
export function rowPool(selection: Selection, snapshots: ClientSnapshot[]): ClientSnapshot[] {
  if (selection.kind === 'view') return selection.stacks;
  const account = selection.stacks[0]?.client_config?.client_account_id;
  return account ? snapshots.filter((s) => s.client_config?.client_account_id === account) : selection.stacks;
}

// ─── sorting ─────────────────────────────────────────────────────────────

export type SortDir = 'asc' | 'desc';
export interface Sort<K extends string> {
  key: K;
  dir: SortDir;
}
export type SortValue = string | number | null | undefined;

const noValue = (v: SortValue) => v === null || v === undefined || v === '';

/** Orders two cells. A row with no value goes last, whichever way the column runs. */
export function compareValues(a: SortValue, b: SortValue, dir: SortDir): number {
  if (noValue(a) || noValue(b)) return noValue(a) === noValue(b) ? 0 : noValue(a) ? 1 : -1;
  const order =
    typeof a === 'number' && typeof b === 'number'
      ? a - b
      : String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
  return dir === 'asc' ? order : -order;
}

/** A copy of the rows in the order of one column. */
export function sortRows<T>(rows: T[], value: (row: T) => SortValue, dir: SortDir): T[] {
  return rows.slice().sort((a, b) => compareValues(value(a), value(b), dir));
}

/** Click a column again to turn it round. A new column starts A to Z for words and largest first for numbers. */
export function nextSort<K extends string>(current: Sort<K>, key: K, words: ReadonlySet<K>): Sort<K> {
  if (current.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  return { key, dir: words.has(key) ? 'asc' : 'desc' };
}

const NO_WORD_COLUMNS: ReadonlySet<never> = new Set();

/**
 * The sort a table is in, and what a click on a header does to it. `words`
 * names the columns that hold text: they start A to Z, the rest start
 * largest first.
 */
export function useSort<K extends string>(initial: Sort<K>, words: ReadonlySet<K> = NO_WORD_COLUMNS) {
  const [sort, setSort] = useState<Sort<K>>(initial);
  const onSort = useCallback((key: K) => setSort((current) => nextSort(current, key, words)), [words]);
  return { sort, onSort, setSort };
}

interface SortThProps<K extends string> {
  column: K;
  sort: Sort<K>;
  onSort: (key: K) => void;
  /** A column of numbers: right-aligned, with the sort mark before the words so the numbers line up under them. */
  numeric?: boolean;
  /** Tagged on the header when the column does not follow the time window. */
  scope?: Scope;
  /** What the column is, shown on hover. */
  hint?: string;
  className?: string;
  children: ReactNode;
}

/** A column header a person clicks to sort by that column. */
export function SortTh<K extends string>({
  column,
  sort,
  onSort,
  numeric,
  scope,
  hint,
  className,
  children,
}: SortThProps<K>) {
  const active = sort.key === column;
  const mark = (
    <span className="dash-sort__mark" aria-hidden="true">
      {active && (sort.dir === 'asc' ? <CaretUpFill size={9} /> : <CaretDownFill size={9} />)}
    </span>
  );
  const classes = ['dash-th', numeric ? 'text-end' : '', className ?? ''].filter(Boolean).join(' ');
  return (
    <th
      className={classes}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      title={hint}
    >
      <button type="button" className="dash-sort" onClick={() => onSort(column)}>
        {numeric && mark}
        {children}
        {!numeric && mark}
      </button>
      <ScopeTag scope={scope} />
    </th>
  );
}

// ─── freshness ───────────────────────────────────────────────────────────

interface FreshnessMarkProps {
  generatedAt?: string;
  stale: boolean;
  failed: boolean;
  error?: string;
}

/**
 * The mark beside a stack whose numbers are old. It shows nothing for a stack
 * that updated last night. The words say how old; the hover says exactly when
 * and, for a failed update, why.
 */
export function FreshnessMark({ generatedAt, stale, failed, error }: FreshnessMarkProps) {
  if (!stale && !failed) return null;
  const when = formatNzDateTime(generatedAt);
  const age = timeAgo(generatedAt);
  const title = failed
    ? `The last nightly update failed${error ? `: ${error}` : '.'} ${when ? `These numbers are from ${when}.` : 'There are no earlier numbers.'}`
    : `Last updated ${when}. The nightly update is behind for this stack.`;
  const words = failed ? (age ? `Update failed. Data from ${age}` : 'Update failed') : `Data from ${age}`;
  return (
    <span className="dash-fresh" title={title}>
      <ExclamationTriangleFill aria-hidden="true" />
      {words}
    </span>
  );
}

// ─── search, links, bars ─────────────────────────────────────────────────

interface SearchBoxProps {
  value: string;
  onChange: (value: string) => void;
  /** The words in the empty box: "Search stacks". */
  placeholder: string;
  /** What the box searches, for a screen reader. Left out, the placeholder is read. */
  label?: string;
  /** Room for a longer placeholder. */
  wide?: boolean;
}

/** The small search box in a panel's header. */
export function SearchBox({ value, onChange, placeholder, label, wide }: SearchBoxProps) {
  return (
    <Form.Control
      type="search"
      size="sm"
      className={wide ? 'dash-search dash-search--wide' : 'dash-search'}
      placeholder={placeholder}
      aria-label={label ?? placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

interface LinkButtonProps {
  onClick?: () => void;
  title?: string;
  /** The name in a table's first column: dark and bold, purple on hover. Left out: a purple link in running text. */
  name?: boolean;
  children: ReactNode;
}

/** Words a person selects to go somewhere in the dashboard: open a stack, show the rest of a list. */
export function LinkButton({ onClick, title, name, children }: LinkButtonProps) {
  return (
    <button type="button" className={name ? 'dash-name-link' : 'dash-link'} onClick={onClick} title={title}>
      {children}
    </button>
  );
}

/** A dot in the colour a chart or a map draws something in, for the key beside it. */
export function Swatch({ colour }: { colour: string }) {
  return (
    <svg className="dash-swatch" width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
      <circle cx="5" cy="5" r="5" fill={colour} />
    </svg>
  );
}

/** A filled bar: a share of a whole, or a count against the largest. `fraction` is 0 to 1. */
export function Bar({ fraction, className }: { fraction: number; className?: string }) {
  const width = Math.min(100, Math.max(0, fraction * 100));
  return (
    <span className={className ? `dash-bar ${className}` : 'dash-bar'} aria-hidden="true">
      {width > 0 && <span className="dash-bar__fill" style={{ width: `${width}%` }} />}
    </span>
  );
}
